import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Check,
  Copy,
  ExternalLink,
  FileCode,
  GitBranch,
  Loader2,
  RefreshCw,
  Search,
  Terminal,
  Upload,
} from "lucide-react";
import {
  useWizard,
  type WizardStep,
  type ConfigureSubStep,
} from "@/hooks/useWizard";
import { useOit } from "@/hooks/useOit";
import type {
  BuildGraphResponse,
  GeneratedFile,
  PushFilesResponse,
  RepoInfo,
} from "@/api/gitAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

const STEPS: { key: WizardStep; labelKey: string }[] = [
  { key: "connect", labelKey: "wizard.steps.connect" },
  { key: "select-repo", labelKey: "wizard.steps.selectRepo" },
  { key: "select-branch", labelKey: "wizard.steps.branch" },
  { key: "discovering", labelKey: "wizard.steps.discover" },
  { key: "configuring", labelKey: "wizard.steps.configure" },
  { key: "ready", labelKey: "wizard.steps.ready" },
];

const INSTALLER_URL = import.meta.env.VITE_INSTALLER_URL ?? "https://get.raise.works";

function StepIndicator({ current }: { current: WizardStep }) {
  const { t } = useTranslation();
  const currentIdx = STEPS.findIndex((s) => s.key === current);
  return (
    <div className="flex items-center gap-2 mb-8">
      {STEPS.map((step, i) => (
        <div key={step.key} className="flex items-center gap-2">
          <div
            className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-medium ${
              i < currentIdx
                ? "bg-primary text-primary-foreground"
                : i === currentIdx
                  ? "bg-primary text-primary-foreground ring-2 ring-primary/30"
                  : "bg-muted text-muted-foreground"
            }`}
          >
            {i < currentIdx ? <Check className="h-4 w-4" /> : i + 1}
          </div>
          <span
            className={`text-sm ${i === currentIdx ? "font-medium" : "text-muted-foreground"}`}
          >
            {t(step.labelKey)}
          </span>
          {i < STEPS.length - 1 && (
            <div
              className={`h-px w-8 ${i < currentIdx ? "bg-primary" : "bg-muted"}`}
            />
          )}
        </div>
      ))}
    </div>
  );
}

function ConnectStep({
  onConnect,
  loading,
}: {
  onConnect: (provider: string) => void;
  loading: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("wizard.connectStep.title")}</CardTitle>
      </CardHeader>
      <CardContent className="flex gap-4">
        <Button
          onClick={() => onConnect("gitlab")}
          disabled={loading}
          className="flex-1"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          GitLab
        </Button>
        <Button
          onClick={() => onConnect("github")}
          disabled={loading}
          variant="outline"
          className="flex-1"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          GitHub
        </Button>
      </CardContent>
    </Card>
  );
}

function RepoSelectStep({
  repos,
  loading,
  onSelect,
  onSearch,
  providerUsername,
}: {
  repos: RepoInfo[];
  loading: boolean;
  onSelect: (repo: RepoInfo) => void;
  onSearch: (query: string) => void;
  providerUsername: string | null;
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");

  const handleSearch = useCallback(
    (value: string) => {
      setSearch(value);
      onSearch(value);
    },
    [onSearch],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <GitBranch className="h-5 w-5" />
          {t("wizard.repoSelectStep.title")}
          {providerUsername && (
            <Badge variant="secondary" className="ml-2">
              {providerUsername}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t("wizard.searchPlaceholder")}
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : repos.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            {t("wizard.repoSelectStep.noRepos")}
          </p>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {repos.map((repo) => (
              <button
                key={repo.provider_repo_id}
                onClick={() => onSelect(repo)}
                className="w-full text-left rounded-lg border border-border p-3 hover:bg-muted/50 transition-colors"
              >
                <div className="font-medium text-sm">{repo.full_name}</div>
                {repo.description && (
                  <div className="text-xs text-muted-foreground mt-1 truncate">
                    {repo.description}
                  </div>
                )}
                <div className="flex gap-2 mt-1">
                  {repo.language && (
                    <Badge variant="outline" className="text-xs">
                      {repo.language}
                    </Badge>
                  )}
                  <Badge variant="outline" className="text-xs">
                    {repo.visibility}
                  </Badge>
                </div>
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function BranchSelectStep({
  branches,
  defaultBranch,
  repoName,
  onSelect,
  loading,
}: {
  branches: string[];
  defaultBranch: string;
  repoName: string;
  onSelect: (branch: string) => void;
  loading: boolean;
}) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(defaultBranch);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <GitBranch className="h-5 w-5" />
          {t("wizard.branchSelectStep.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {t("wizard.branchSelectStep.choosePrefix")} <span className="font-medium">{repoName}</span>{" "}
          {t("wizard.branchSelectStep.chooseSuffix")}
        </p>
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {branches.map((branch) => (
            <button
              key={branch}
              onClick={() => setSelected(branch)}
              className={`w-full text-left rounded-lg border p-3 transition-colors ${
                selected === branch
                  ? "border-primary bg-primary/5"
                  : "border-border hover:bg-muted/50"
              }`}
            >
              <div className="flex items-center gap-2">
                <GitBranch className="h-4 w-4 text-muted-foreground" />
                <span className="font-mono text-sm">{branch}</span>
                {branch === defaultBranch && (
                  <Badge variant="secondary" className="ml-auto text-xs">
                    {t("wizard.branchSelectStep.defaultBadge")}
                  </Badge>
                )}
              </div>
            </button>
          ))}
        </div>
        <Button
          onClick={() => onSelect(selected)}
          disabled={loading || !selected}
          className="w-full"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          {t("wizard.branchSelectStep.analyzeButton", { branch: selected })}
        </Button>
      </CardContent>
    </Card>
  );
}

const DISCOVERY_STEPS = ["cloning", "scanning", "detecting", "complete"];

function DiscoveringStep({
  status,
  message,
}: {
  status: string;
  message: string;
}) {
  const { t } = useTranslation();
  const currentIdx = DISCOVERY_STEPS.indexOf(status);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Loader2 className="h-5 w-5 animate-spin" />
          {t("wizard.discoveringStep.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {DISCOVERY_STEPS.map((step, i) => (
          <div key={step} className="flex items-center gap-3">
            <div
              className={`h-2 w-2 rounded-full ${
                i < currentIdx
                  ? "bg-primary"
                  : i === currentIdx
                    ? "bg-primary animate-pulse"
                    : "bg-muted"
              }`}
            />
            <span
              className={`text-sm capitalize ${
                i === currentIdx ? "font-medium" : "text-muted-foreground"
              }`}
            >
              {step}
            </span>
            {i < currentIdx && <Check className="h-3 w-3 text-primary" />}
          </div>
        ))}
        <p className="text-xs text-muted-foreground mt-4">{message}</p>
      </CardContent>
    </Card>
  );
}

function ConfigureStep({
  files,
  onPush,
  onSkip,
  loading,
  pushResult,
  graphResult,
  subStep,
}: {
  files: GeneratedFile[];
  onPush: () => void;
  onSkip: () => void;
  loading: boolean;
  pushResult: PushFilesResponse | null;
  graphResult: BuildGraphResponse | null;
  subStep: ConfigureSubStep;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      {/* Graph build status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {subStep === "building-graph" ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Check className="h-5 w-5 text-primary" />
            )}
            {subStep === "building-graph"
              ? t("wizard.buildingGraph")
              : t("wizard.graphBuilt")}
          </CardTitle>
        </CardHeader>
        {graphResult && (
          <CardContent className="space-y-1">
            <p className="text-sm">
              {t("wizard.configureStep.nodesEdgesSynced", { nodes: graphResult.nodes_synced, edges: graphResult.edges_synced })}
            </p>
            {graphResult.embedding_job_id && (
              <p className="text-xs text-muted-foreground">
                {t("wizard.embeddingsGenerating")}
              </p>
            )}
          </CardContent>
        )}
      </Card>

      {/* Generated files list */}
      {subStep !== "building-graph" && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileCode className="h-5 w-5" />
              {t("wizard.configureStep.filesReady", { count: files.length })}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {files.map((file) => (
              <div
                key={file.path}
                className="flex items-center gap-2 rounded-md bg-muted p-2"
              >
                <FileCode className="h-4 w-4 text-muted-foreground" />
                <span className="font-mono text-sm">{file.path}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Push progress */}
      {subStep === "pushing" && (
        <Card>
          <CardContent className="pt-6 flex items-center gap-3">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">{t("wizard.configureStep.creatingPr")}</span>
          </CardContent>
        </Card>
      )}

      {/* Push result */}
      {pushResult && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Check className="h-5 w-5 text-primary" />
              {t("wizard.configureStep.prCreated")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm">
              {t("wizard.configureStep.branchPrefix")} <Badge variant="secondary">{pushResult.branch}</Badge>{" "}
              {t("wizard.configureStep.branchPushed")}
            </p>
            <a
              href={pushResult.pr_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
            >
              <ExternalLink className="h-4 w-4" />
              {t("wizard.configureStep.openPr")}
            </a>
          </CardContent>
        </Card>
      )}

      {/* Action buttons — only after graph is built */}
      {subStep === "graph-done" && !pushResult && (
        <div className="flex gap-3">
          <Button onClick={onPush} disabled={loading} className="flex-1">
            <Upload className="h-4 w-4 mr-2" />
            {t("wizard.configureStep.pushButton")}
          </Button>
          <Button onClick={onSkip} variant="outline" className="flex-1">
            {t("wizard.configureStep.skipButton")}
          </Button>
        </div>
      )}
    </div>
  );
}

function ReadyStep({
  bootstrapResult,
  discoveryResult,
  graphResult,
}: {
  bootstrapResult: {
    project_slug: string;
    stack: { languages: { name: string; file_count: number }[]; frameworks: string[]; tools: string[]; file_count: number; has_monorepo: boolean } | null;
  };
  discoveryResult: { languages: { name: string; file_count: number }[]; frameworks: string[]; tools: string[]; file_count: number } | null;
  graphResult: BuildGraphResponse | null;
}) {
  const { t } = useTranslation();
  const { generateOit, oitState, loading: oitLoading } = useOit();
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    generateOit(bootstrapResult.project_slug);
  }, [bootstrapResult.project_slug, generateOit]);

  useEffect(() => {
    if (!oitState) return;
    const update = () => {
      const elapsed = Math.floor((Date.now() - oitState.generated_at) / 1000);
      setSecondsLeft(Math.max(0, oitState.expires_in - elapsed));
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [oitState]);

  const magicLine = oitState
    ? `curl -fsSL ${INSTALLER_URL} | sh --token ${oitState.token}`
    : null;

  const isExpired = oitState !== null && secondsLeft === 0;

  const handleCopy = useCallback(() => {
    if (!magicLine) return;
    navigator.clipboard.writeText(magicLine).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  }, [magicLine]);

  const formatTime = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const stack = discoveryResult ?? bootstrapResult.stack;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Check className="h-5 w-5 text-primary" />
            {t("wizard.readyStep.projectReady", { slug: bootstrapResult.project_slug })}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {stack && (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-2">
                {stack.languages.map((lang) => (
                  <Badge key={lang.name} variant="secondary">
                    {lang.name} ({lang.file_count} files)
                  </Badge>
                ))}
              </div>
              {stack.frameworks.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {stack.frameworks.map((fw) => (
                    <Badge key={fw} variant="outline">
                      {fw}
                    </Badge>
                  ))}
                </div>
              )}
              {stack.tools.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {stack.tools.map((tool) => (
                    <Badge key={tool} variant="outline">
                      {tool}
                    </Badge>
                  ))}
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                {t("wizard.readyStep.filesDetected", { count: stack.file_count })}
              </p>
            </div>
          )}
          {graphResult && (
            <div className="mt-3 pt-3 border-t border-border space-y-1">
              <p className="text-xs text-muted-foreground">
                {t("wizard.readyStep.knowledgeGraph", { nodes: graphResult.nodes_synced, edges: graphResult.edges_synced })}
              </p>
              {graphResult.embedding_job_id && (
                <p className="text-xs text-muted-foreground">
                  {t("wizard.embeddingsGenerating")}
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Terminal className="h-5 w-5" />
            {t("wizard.readyStep.shareTitle")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {oitLoading ? (
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">{t("wizard.readyStep.generatingToken")}</span>
            </div>
          ) : magicLine ? (
            <>
              <pre className="overflow-x-auto rounded-md bg-muted p-3 font-mono text-sm">
                {magicLine}
              </pre>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopy}
                  disabled={isExpired}
                >
                  <Copy className="h-3 w-3" />
                  {copied ? t("wizard.copied") : t("wizard.copy")}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => generateOit(bootstrapResult.project_slug)}
                  disabled={oitLoading}
                >
                  <RefreshCw className="h-3 w-3" />
                  {t("wizard.readyStep.regenerate")}
                </Button>
                <span
                  className={`ml-auto text-xs ${isExpired ? "text-destructive" : "text-muted-foreground"}`}
                >
                  {isExpired ? t("wizard.expired") : t("wizard.expiresIn", { time: formatTime(secondsLeft) })}
                </span>
              </div>
            </>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}

export function WizardPage() {
  const { t } = useTranslation();
  const {
    state,
    checkExistingConnection,
    connectProvider,
    loadRepos,
    selectRepo,
    startDiscoveryWithBranch,
    pushToRepo,
    skipPush,
    retry,
  } = useWizard();

  useEffect(() => {
    checkExistingConnection();
  }, [checkExistingConnection]);

  useEffect(() => {
    if (state.provider && state.step === "select-repo" && state.repos.length === 0) {
      loadRepos(state.provider);
    }
  }, [state.provider, state.step, state.repos.length, loadRepos]);

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h2 className="text-2xl font-bold">{t("wizard.pageTitle")}</h2>
        <p className="text-muted-foreground">
          {t("wizard.pageSubtitle")}
        </p>
      </div>

      <StepIndicator current={state.step} />

      {state.error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 flex items-center justify-between">
          <p className="text-sm text-destructive">{state.error}</p>
          <Button variant="outline" size="sm" onClick={retry}>
            {t("wizard.retry")}
          </Button>
        </div>
      )}

      {state.step === "connect" && (
        <ConnectStep onConnect={connectProvider} loading={state.loading} />
      )}

      {state.step === "select-repo" && (
        <RepoSelectStep
          repos={state.repos}
          loading={state.loading}
          onSelect={selectRepo}
          onSearch={(q) => state.provider && loadRepos(state.provider, q)}
          providerUsername={state.providerUsername}
        />
      )}

      {state.step === "select-branch" && state.selectedRepo && (
        <BranchSelectStep
          branches={state.branches}
          defaultBranch={state.selectedBranch}
          repoName={state.selectedRepo.full_name}
          onSelect={startDiscoveryWithBranch}
          loading={state.loading}
        />
      )}

      {state.step === "discovering" && (
        <DiscoveringStep
          status={state.discoveryStatus}
          message={state.discoveryMessage}
        />
      )}

      {state.step === "configuring" && (
        <ConfigureStep
          files={state.generatedFiles}
          onPush={pushToRepo}
          onSkip={skipPush}
          loading={state.loading}
          pushResult={state.pushResult}
          graphResult={state.graphResult}
          subStep={state.configureSubStep}
        />
      )}

      {state.step === "ready" && state.bootstrapResult && (
        <ReadyStep
          bootstrapResult={state.bootstrapResult}
          discoveryResult={state.discoveryResult}
          graphResult={state.graphResult}
        />
      )}
    </div>
  );
}
